'use client';

import Carousel from 'components/shared/Carousel/Carousel';
import CarouselContent from 'components/shared/Carousel/CarouselContent';
import CarouselPagination from 'components/shared/Carousel/CarouselPagination';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { TestimonialProps } from './types';
import { cn, getColorway, getYMargins } from 'utils/styles';
import TestimonialItem from './TestimonialItem';
import useCarousel from 'hooks/useCarousel';
import { AutoplayOptionsType } from 'embla-carousel-autoplay';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { useAutoplayPlugin } from 'hooks/useAutoplayPlugin';
import { TestimonialVariants } from './constants';
import { OverflowVisibleSettings } from 'types/constants';

const Testimonial = ({
  sys,
  variant,
  sectionName,
  sectionColor,
  testimonialItems,
  disclaimer,
  slideColor,
  margins,
}: TestimonialProps) => {
  const { current, snapCount, setApi, scrollTo } = useCarousel();

  const autoPlayOptions: AutoplayOptionsType = {
    delay: 5000,
    stopOnMouseEnter: true,
    stopOnFocusIn: true,
    stopOnInteraction: false,
    rootNode: (emblaRoot: HTMLElement) => emblaRoot.parentElement,
  };

  const isCards = variant === TestimonialVariants.Cards;
  const autoplayPlugin = useAutoplayPlugin(autoPlayOptions);
  const plugins = isCards || !autoplayPlugin ? [] : [autoplayPlugin];

  const carouselOpts = isCards
    ? { align: 'start' as const, slidesToScroll: 'auto' as const, containScroll: 'trimSnaps' as const }
    : { align: 'start' as const, slidesToScroll: 1, containScroll: false as const };

  // An unresolved or empty testimonials collection would throw during SSR and 500 the page; render nothing instead.
  if (!testimonialItems?.items?.length) return null;

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={cn(getColorway(sectionColor), getYMargins(margins), 'overflow-hidden')}>
      <div className="content-wrapper">
        <Carousel setApi={setApi} plugins={plugins} opts={carouselOpts}>
          <CarouselContent
            className={cn(
              'rounded-lg',
              !isCards &&
                cn(
                  getColorway(slideColor),
                  'px-[24px] py-[32px]', // mobile
                  'sm:px-[48px]', // tablet
                  'md:px-[56px] ml:px-[64px] lg:px-[96px] lg:pt-[80px]', // desktop + WebTV
                ),
            )}
            overflowVisible={isCards ? OverflowVisibleSettings.All : OverflowVisibleSettings.None}>
            <div
              className={cn('flex space-x-8', isCards && 'items-start')}
              {...liveUpdateAttrs(sys?.id, 'testimonialItems')}>
              {testimonialItems.items.map((item, index) => (
                <TestimonialItem
                  key={`testimonial-item-${index}`}
                  {...item}
                  copy={item.copy}
                  media={item.media}
                  variant={variant}
                  cta={item.cta}
                  aspectRatio={item.aspectRatio}
                  index={index}
                  slideColor={slideColor}
                />
              ))}
            </div>

            <div className="flex flex-col lg:flex-row gap-4 lg:items-center mt-8 lg:mt-12">
              {testimonialItems.items.length > 1 && (
                <CarouselPagination
                  total={snapCount || testimonialItems.items.length}
                  current={current}
                  onSelect={scrollTo}
                />
              )}
              {disclaimer && (
                <div
                  className={`font-sans text-xs font-medium ${testimonialItems.items[current].media ? 'lg:ml-auto' : 'lg:ml-[11%]'}`}
                  data-testid="testimonialDisclaimer"
                  {...liveUpdateAttrs(sys?.id, 'disclaimer')}>
                  <RichTextCopy copy={disclaimer} />
                </div>
              )}
            </div>
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
};
export default Testimonial;
